import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/authService';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

const STORAGE_KEYS = {
  SELECTED_ACCOUNT: 'selectedAccount',
  SELECTED_CAMPAIGN: 'selectedCampaign',
};

const JourneysListPage = () => {
  const navigate = useNavigate();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Accounts state
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_ACCOUNT);
    return stored || '';
  });
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_CAMPAIGN);
    return stored || '';
  });
  
  // Funnels state
  const [funnels, setFunnels] = useState([]);
  const [funnelsLoading, setFunnelsLoading] = useState(false);
  const [selectedFunnel, setSelectedFunnel] = useState('');

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      setAccountsLoading(true);
      try {
        const response = await api.get('/crm/accounts/');
        setAccounts(response.data);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      } finally {
        setAccountsLoading(false);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch journeys when all filters are selected
  useEffect(() => {
    const fetchJourneys = async () => {
      if (selectedAccount && selectedCampaign && selectedFunnel) {
        try {
          setLoading(true);
          const response = await api.get('/acs/journeys/', {
            params: {
              account_id: selectedAccount,
              campaign_id: selectedCampaign,
              funnel_id: selectedFunnel
            }
          });
          setJourneys(response.data);
          setError(null);
        } catch (err) {
          console.error('Error fetching journeys:', err);
          setError('Failed to load journeys');
          setJourneys([]);
        } finally {
          setLoading(false);
        }
      } else {
        setJourneys([]);
      }
    };
    fetchJourneys();
  }, [selectedAccount, selectedCampaign, selectedFunnel]);

  // Handle account selection
  const handleAccountChange = async (accountId) => {
    try {
      setSelectedAccount(accountId);
      localStorage.setItem(STORAGE_KEYS.SELECTED_ACCOUNT, accountId);
      
      // Clear campaign selection when account changes
      setSelectedCampaign('');
      localStorage.removeItem(STORAGE_KEYS.SELECTED_CAMPAIGN);
      
      // Clear funnel selection
      setSelectedFunnel('');
      
      // Fetch campaigns for the new account
      if (accountId) {
        setCampaignsLoading(true);
        const response = await api.get(`/crm/campaigns/?account_id=${accountId}`);
        setCampaigns(response.data);
      } else {
        setCampaigns([]);
      }
    } catch (error) {
      console.error('Error handling account change:', error);
    } finally {
      setCampaignsLoading(false);
    }
  };

  // Handle campaign selection
  const handleCampaignChange = async (campaignId) => {
    try {
      setSelectedCampaign(campaignId);
      localStorage.setItem(STORAGE_KEYS.SELECTED_CAMPAIGN, campaignId);
      
      // Clear funnel selection
      setSelectedFunnel('');
      
      // Fetch funnels for the new campaign
      if (campaignId) {
        setFunnelsLoading(true);
        const response = await api.get(`/crm/funnels/?campaign_id=${campaignId}`);
        setFunnels(response.data);
      } else {
        setFunnels([]);
      }
    } catch (error) {
      console.error('Error handling campaign change:', error);
    } finally {
      setFunnelsLoading(false);
    }
  };

  // Validate selectedAccount
  useEffect(() => {
    if (!accountsLoading && selectedAccount) {
      const accountExists = accounts.some((a) => String(a.id) === String(selectedAccount));
      if (!accountExists) {
        handleAccountChange('');
      }
    }
  }, [accountsLoading, accounts, selectedAccount]);

  // Validate selectedCampaign
  useEffect(() => {
    if (!campaignsLoading && selectedCampaign) {
      if (campaigns.length > 0) {
        const campaignExists = campaigns.some((c) => String(c.id) === String(selectedCampaign));
        if (!campaignExists) {
          handleCampaignChange('');
        }
      } else {
        handleCampaignChange('');
      }
    }
  }, [campaignsLoading, campaigns, selectedCampaign]);

  // Validate selectedFunnel
  useEffect(() => {
    if (!funnelsLoading && selectedFunnel) {
      if (funnels.length > 0) {
        const funnelExists = funnels.some((f) => String(f.id) === String(selectedFunnel));
        if (!funnelExists) {
          setSelectedFunnel('');
        }
      } else {
        setSelectedFunnel('');
      }
    }
  }, [funnelsLoading, funnels, selectedFunnel]);

  const handleDelete = async (journeyId) => {
    if (!window.confirm('Are you sure you want to delete this journey?')) return;
    
    try {
      await api.delete(`/acs/journeys/${journeyId}/`);
      setJourneys(journeys.filter(j => j.id !== journeyId));
    } catch (err) {
      console.error('Error deleting journey:', err);
      alert('Failed to delete journey');
    }
  };

  // Remove getFilteredJourneys since we're now filtering on the backend
  const displayJourneys = journeys;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" color="text.primary">
          Journeys
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/crm/journeys/create')}
        >
          Create Journey
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Account</InputLabel>
              <Select
                value={selectedAccount}
                onChange={(e) => handleAccountChange(e.target.value)}
                label="Account"
                disabled={accountsLoading}
              >
                <MenuItem value="">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Campaign</InputLabel>
              <Select
                value={selectedCampaign}
                onChange={(e) => handleCampaignChange(e.target.value)}
                label="Campaign"
                disabled={campaignsLoading || !selectedAccount}
              >
                <MenuItem value="">All Campaigns</MenuItem>
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Funnel</InputLabel>
              <Select
                value={selectedFunnel}
                onChange={(e) => setSelectedFunnel(e.target.value)}
                label="Funnel"
                disabled={funnelsLoading || !selectedCampaign}
              >
                <MenuItem value="">All Funnels</MenuItem>
                {funnels.map((funnel) => (
                  <MenuItem key={funnel.id} value={funnel.id}>
                    {funnel.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {!selectedAccount || !selectedCampaign || !selectedFunnel ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Alert severity="info" sx={{ width: '100%', maxWidth: 600 }}>
            Please select an account, campaign, and funnel to view journeys.
          </Alert>
        </Box>
      ) : displayJourneys.length === 0 ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Alert severity="info" sx={{ width: '100%', maxWidth: 600 }}>
            No journeys found for the selected filters.
          </Alert>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {displayJourneys.map((journey) => {
            // Find the selected funnel object
            const selectedFunnelObj = funnels.find(f => String(f.id) === String(selectedFunnel));
            
            return (
              <Grid item xs={12} md={6} lg={4} key={journey.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Typography variant="h6" component="h2" gutterBottom>
                        {journey.name}
                      </Typography>
                      <Chip
                        label={journey.is_active ? 'Active' : 'Inactive'}
                        color={journey.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography color="textSecondary" gutterBottom>
                      {journey.description || 'No description'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Account: {accounts.find(a => String(a.id) === String(journey.account))?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Campaign: {campaigns.find(c => String(c.id) === String(journey.campaign))?.name || 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Funnel: {funnels.find(f => String(f.id) === String(journey.funnel))?.name || 'N/A'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/crm/journeys/${journey.id}/builder`, {
                        state: {
                          funnel: selectedFunnelObj
                        }
                      })}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(journey.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default JourneysListPage; 